import { Dispatch, SetStateAction, useMemo, useState } from "react";
import {
    DefaultButton,
    Dropdown,
    FontIcon,
    IDropdownOption,
    IconButton,
    Label,
    MessageBar,
    MessageBarType,
    PrimaryButton,
    SelectionMode,
    Spinner,
    SpinnerSize,
    Stack,
    Text
} from "@fluentui/react";
import { useHistory, useLocation } from "react-router-dom";
import { GraphLookupList } from "./GraphLookupList";
import PreferredSelect from "../common/PreferredSelect";
import StandardList from "../common/StandardList";
import { useGetCatalogsQuery } from "../../reducers/GraphElmApi";
import {
    useBulkApproverUpdateMutation,
    usePreviewBulkApproverUpdateMutation
} from "../../reducers/AccessPackageApi";
import { AADState } from "./Types";
import {
    BulkApproverUpdateOperationType,
    BulkApproverUpdatePackageResult,
    BulkApproverUpdateRequest,
    BulkApproverUpdateStageScope
} from "../../models/Elm";
import { IContact } from "../../models";
import { StateValues } from "../../types";
import {
    BulkApproverPreviewRow,
    generateBulkApproverPreviewColumns
} from "../../columns/BulkApproverPreviewColumns";
import {
    BulkApproverSkippedRow,
    generateBulkApproverSkippedColumns
} from "../../columns/BulkApproverPreviewColumns";

const operationOptions: IDropdownOption[] = [
    { key: "Add", text: "Add approver" },
    { key: "Remove", text: "Remove approver" },
    { key: "Replace", text: "Replace approver" }
];

const stageOptions: IDropdownOption[] = [
    { key: "Both", text: "Both stages" },
    { key: "FirstStage", text: "First stage" },
    { key: "SecondStage", text: "Second stage" }
];

const warningSelectionThreshold = 15;

const selectionSummaryCardStyles = {
    root: {
        backgroundColor: "#f6fbff",
        border: "1px solid #c7e0f4",
        borderRadius: 6,
        padding: 14
    }
};

const executeNotificationStyles = {
    root: {
        position: "fixed" as const,
        top: 76,
        right: 16,
        maxWidth: 440,
        zIndex: 1000,
        boxShadow: "0 2px 10px rgba(0,0,0,0.2)"
    }
};

const blockingOverlayStyles = {
    root: {
        position: "absolute" as const,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(255, 255, 255, 0.6)",
        zIndex: 10,
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
    }
};

const compactInfoBoxStyles = {
    root: {
        backgroundColor: "#faf9f8",
        border: "1px solid #edebe9",
        borderRadius: 6,
        padding: 12
    }
};

type ExecuteNotificationState = {
    type: MessageBarType;
    message: string;
};

/**
 * Converts a picked AAD person into the contact shape expected by the bulk update API.
 */
function toContact(aad: AADState): IContact {
    return {
        id: 0,
        name: aad.AADName,
        objectId: aad.AADObjectId,
        email: aad.AADEmail,
        type: aad.AADType || "User"
    };
}

/**
 * Wizard page for previewing and executing remove/replace approver operations across access packages.
 */
function BulkApproverUpdate() {
    const history = useHistory();
    const location = useLocation();
    const queryCatalogId = useMemo(() => new URLSearchParams(location.search).get("catalogId") || "", [location.search]);

    const [selectedCatalogId, setSelectedCatalogId] = useState<string>(queryCatalogId);
    const [operationType, setOperationType] = useState<BulkApproverUpdateOperationType>("Replace");
    const [stageScope, setStageScope] = useState<BulkApproverUpdateStageScope>("Both");
    const [sourceApprover, setSourceApprover] = useState<AADState[]>([]);
    const [targetApprover, setTargetApprover] = useState<AADState[]>([]);
    const [selectedPreviewRows, setSelectedPreviewRows] = useState<BulkApproverPreviewRow[]>([]);
    const [executeResultByPackageId, setExecuteResultByPackageId] = useState<Record<string, BulkApproverUpdatePackageResult>>({});
    const [error, setError] = useState<string>("");
    const [executeNotification, setExecuteNotification] = useState<ExecuteNotificationState | undefined>();
    const [executeCompleted, setExecuteCompleted] = useState<boolean>(false);

    const { data: catalogs } = useGetCatalogsQuery(undefined, { pollingInterval: 3600000 });
    const [previewBulkApproverUpdate, previewResult] = usePreviewBulkApproverUpdateMutation();
    const [bulkApproverUpdate, executeResult] = useBulkApproverUpdateMutation();

    const resetPreviewWorkflowState = () => {
        setSelectedPreviewRows([]);
        setExecuteResultByPackageId({});
        setExecuteCompleted(false);
        setExecuteNotification(undefined);
        setError("");
        previewResult.reset();
        executeResult.reset();
    };

    const requiresSourceApprover = operationType === "Remove" || operationType === "Replace";
    const requiresTargetApprover = operationType === "Add" || operationType === "Replace";

    const canPreview =
        !!selectedCatalogId &&
        (!requiresSourceApprover || sourceApprover.length === 1) &&
        (!requiresTargetApprover || targetApprover.length === 1);

    const request = useMemo<BulkApproverUpdateRequest | undefined>(() => {
        if (!canPreview) {
            return undefined;
        }

        return {
            catalogId: selectedCatalogId,
            operationType,
            stageScope,
            sourceApprover: sourceApprover.length === 1 ? toContact(sourceApprover[0]) : undefined,
            targetApprover: targetApprover.length === 1 ? toContact(targetApprover[0]) : undefined
        };
    }, [canPreview, selectedCatalogId, operationType, stageScope, sourceApprover, targetApprover]);

    const previewRows = useMemo<BulkApproverPreviewRow[]>(() => {
        return (previewResult.data?.impactedPackages ?? [])
            .filter((item) => operationType !== "Remove" || (!item.wouldEmptyFirstStage && !item.wouldEmptySecondStage))
            .map((item) => ({
                id: item.accessPackageId,
                name: item.accessPackageName,
                sourceFoundInFirstStage: item.sourceFoundInFirstStage,
                sourceFoundInSecondStage: item.sourceFoundInSecondStage
            }));
    }, [previewResult.data, operationType]);

    const previewRowsWithOperationResult = useMemo<BulkApproverPreviewRow[]>(() => {
        if (!executeCompleted) {
            return previewRows;
        }

        const selectedRowIds = new Set(selectedPreviewRows.map((x) => x.id));

        return previewRows.map((row) => {
            const executePackageResult = executeResultByPackageId[row.id];

            if (executePackageResult) {
                return {
                    ...row,
                    operationResult: executePackageResult.success ? "Success" : "Failed"
                };
            }

            if (selectedRowIds.has(row.id)) {
                return {
                    ...row,
                    operationResult: "Failed"
                };
            }

            return {
                ...row,
                operationResult: "Not selected"
            };
        });
    }, [previewRows, executeCompleted, selectedPreviewRows, executeResultByPackageId]);

    const skippedRows = useMemo<BulkApproverSkippedRow[]>(() => {
        if (operationType !== "Remove") return [];
        return (previewResult.data?.impactedPackages ?? [])
            .filter((item) => item.wouldEmptyFirstStage || item.wouldEmptySecondStage)
            .map((item) => {
                const reasons: string[] = [];
                if (item.wouldEmptyFirstStage) reasons.push("would leave first stage empty");
                if (item.wouldEmptySecondStage) reasons.push("would leave second stage empty");
                return {
                    id: item.accessPackageId,
                    name: item.accessPackageName,
                    skipReason: reasons.join(" and ")
                };
            });
    }, [previewResult.data, operationType]);

    const displayedImpactedCount = previewRows.length;
    const displayedSkippedCount = skippedRows.length;

    const previewEntityState: StateValues = previewResult.isLoading ? "LOADING_ALL" : "LOADED_ALL";
    const isPreviewing = previewResult.isLoading;
    const isExecuting = executeResult.isLoading;
    const isBusy = isPreviewing || isExecuting;

    const canExecute =
        !!previewResult.data &&
        !isPreviewing &&
        !isExecuting &&
        !executeCompleted &&
        selectedPreviewRows.length > 0;

    const isLargeSelectionWarning = selectedPreviewRows.length > warningSelectionThreshold;

    const selectionHint =
        selectedPreviewRows.length === 0
            ? "Select at least one package to enable execution."
            : isLargeSelectionWarning
            ? "Large selection detected. Review impacted packages carefully before executing bulk update."
            : "Selection is valid. You can execute this update.";

    const onPreview = async () => {
        setError("");
        setSelectedPreviewRows([]);
        setExecuteResultByPackageId({});
        setExecuteCompleted(false);
        setExecuteNotification(undefined);
        executeResult.reset();
        if (!request) {
            setError("Please provide catalog and approver inputs before preview.");
            return;
        }

        const result = await previewBulkApproverUpdate(request);
        if ("error" in result) {
            const apiMessage = (result.error as any)?.data?.message;
            setError(apiMessage ?? "There was an error generating preview for bulk approver update.");
        }
    };

    const onCatalogChange: Dispatch<SetStateAction<string>> = (nextCatalogId) => {
        const catalogId = typeof nextCatalogId === "function" ? nextCatalogId(selectedCatalogId) : nextCatalogId;
        setSelectedCatalogId(catalogId);
        resetPreviewWorkflowState();
    };

    const onOperationChange = (nextOperationType: BulkApproverUpdateOperationType) => {
        setOperationType(nextOperationType);
        resetPreviewWorkflowState();
    };

    const onStageScopeChange = (nextStageScope: BulkApproverUpdateStageScope) => {
        setStageScope(nextStageScope);
        resetPreviewWorkflowState();
    };

    const onSourceApproverChange = (nextSourceApprover: AADState[]) => {
        setSourceApprover(nextSourceApprover);
        resetPreviewWorkflowState();
    };

    const onTargetApproverChange = (nextTargetApprover: AADState[]) => {
        setTargetApprover(nextTargetApprover);
        resetPreviewWorkflowState();
    };

    const onExecute = async () => {
        setError("");
        if (!request) {
            setError("Please provide catalog and approver inputs before execution.");
            return;
        }

        if (selectedPreviewRows.length === 0) {
            setError("Select at least one impacted access package before execution.");
            return;
        }

        const executeRequest: BulkApproverUpdateRequest = {
            ...request,
            accessPackageIds: selectedPreviewRows.map((x) => x.id)
        };

        const result = await bulkApproverUpdate(executeRequest);
        if ("error" in result) {
            setExecuteNotification({
                type: MessageBarType.error,
                message: "Bulk approver update failed. No changes were applied."
            });
            setError("There was an error executing bulk approver update.");
            return;
        }

        const successCount = result.data?.successCount ?? 0;
        const failureCount = result.data?.failureCount ?? 0;
        const resultMap = (result.data?.results ?? []).reduce<Record<string, BulkApproverUpdatePackageResult>>((acc, packageResult) => {
            if (packageResult.accessPackageId) {
                acc[packageResult.accessPackageId] = packageResult;
            }
            return acc;
        }, {});

        setExecuteResultByPackageId(resultMap);

        setExecuteCompleted(true);

        if (failureCount > 0 && successCount > 0) {
            setExecuteNotification({
                type: MessageBarType.warning,
                message: `Bulk update completed with partial success. Success: ${successCount}, Failed: ${failureCount}.`
            });
        } else if (failureCount > 0) {
            setExecuteNotification({
                type: MessageBarType.error,
                message: `Bulk update failed. Failed packages: ${failureCount}.`
            });
        } else {
            setExecuteNotification({
                type: MessageBarType.success,
                message: `Bulk update completed successfully. Updated packages: ${successCount}.`
            });
        }
    };

    return (
        <Stack tokens={{ childrenGap: 12 }} styles={{ root: { margin: 12, position: "relative" } }}>
            {isBusy && (
                <Stack styles={blockingOverlayStyles}>
                    <Spinner label={isPreviewing ? "Generating preview..." : "Executing bulk update..."} size={SpinnerSize.medium} />
                </Stack>
            )}

            <Stack
                tokens={{ childrenGap: 12 }}
                styles={{ root: { pointerEvents: isBusy ? "none" : "auto", opacity: isBusy ? 0.7 : 1 } }}
                aria-busy={isBusy}
            >
            {executeNotification && !executeCompleted && (
                <MessageBar
                    messageBarType={executeNotification.type}
                    isMultiline={false}
                    onDismiss={() => setExecuteNotification(undefined)}
                    dismissButtonAriaLabel="Close"
                    styles={executeNotificationStyles}
                >
                    {executeNotification.message}
                </MessageBar>
            )}

            <Stack horizontal horizontalAlign="space-between">
                <Text variant="xLarge">Bulk Approver Update</Text>
                <IconButton
                    title="Close"
                    className="routing"
                    onClick={() => history.goBack()}
                    iconProps={{ iconName: "Cancel" }}
                    allowDisabledFocus
                />
            </Stack>

            {error && <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>}

            <PreferredSelect
                entries={catalogs}
                title="Catalog"
                selectedEntryId={selectedCatalogId}
                showLabel
                urlParam="catalogId"
                setEntryId={onCatalogChange}
            />

            <Dropdown
                label="Operation"
                selectedKey={operationType}
                options={operationOptions}
                onChange={(_ev, option) => option && onOperationChange(option.key as BulkApproverUpdateOperationType)}
            />

            <Dropdown
                label="Stage Scope"
                selectedKey={stageScope}
                options={stageOptions}
                onChange={(_ev, option) => option && onStageScopeChange(option.key as BulkApproverUpdateStageScope)}
            />

            {requiresSourceApprover && (
                <Stack>
                    <Label required>Source Approver (select exactly one)</Label>
                    <GraphLookupList
                        aadState={sourceApprover}
                        setState={onSourceApproverChange}
                        title="Source Approver"
                        editDisabled={!selectedCatalogId}
                    />
                </Stack>
            )}

            {requiresTargetApprover && (
                <Stack>
                    <Label required>{operationType === "Add" ? "Approver to Add (select exactly one)" : "Target Approver (select exactly one)"}</Label>
                    <GraphLookupList
                        aadState={targetApprover}
                        setState={onTargetApproverChange}
                        title={operationType === "Add" ? "Approver to Add" : "Target Approver"}
                        editDisabled={!selectedCatalogId}
                    />
                </Stack>
            )}

            <Stack horizontal tokens={{ childrenGap: 8 }}>
                <PrimaryButton text="Preview Impact" onClick={onPreview} disabled={!canPreview || isPreviewing || isExecuting} />
                <DefaultButton
                    text="Execute Bulk Update"
                    onClick={onExecute}
                    disabled={!canExecute || isPreviewing || isExecuting}
                />
            </Stack>

            {previewResult.data && (
                <Stack tokens={{ childrenGap: 8 }}>
                    {executeCompleted && executeNotification ? (
                        <MessageBar
                            messageBarType={executeNotification.type}
                            isMultiline={false}
                            styles={{ root: { borderRadius: 4 } }}
                        >
                            {executeNotification.message}
                        </MessageBar>
                    ) : (
                        <Stack styles={selectionSummaryCardStyles} tokens={{ childrenGap: 6 }}>
                            <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                                <FontIcon iconName="InfoSolid" style={{ color: "#0078d4" }} />
                                <Text variant="mediumPlus">Review Selection</Text>
                            </Stack>
                            <Text>Access Packages Scanned: {previewResult.data.candidateCount}</Text>
                            <Text>Eligible Packages to Update: {displayedImpactedCount}</Text>
                            {operationType === "Remove" && (
                                <Text>Not Eligible (Skipped to avoid empty stage): {displayedSkippedCount}</Text>
                            )}
                            <Text>Selected: {selectedPreviewRows.length}</Text>
                            {isLargeSelectionWarning && (
                                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 6 }}>
                                    <FontIcon iconName="WarningSolid" style={{ color: "#d83b01" }} />
                                    <Text style={{ color: "#d83b01" }}>
                                        Bulk update is being performed on more packages. Review changes before submitting.
                                    </Text>
                                </Stack>
                            )}
                            <Text style={{ color: canExecute ? "#107c10" : "#605e5c" }}>{selectionHint}</Text>
                        </Stack>
                    )}

                    {!executeCompleted &&
                        !(operationType === "Remove" && displayedSkippedCount > 0) &&
                        previewResult.data.warnings?.map((warning) => (
                            <MessageBar key={warning} messageBarType={MessageBarType.warning}>
                                {warning}
                            </MessageBar>
                        ))}

                    {displayedImpactedCount > 0 ? (
                        <StandardList
                            key={`impacted-access-packages-${executeCompleted ? "executed" : "preview"}`}
                            title="Impacted Access Packages"
                            entities={previewRowsWithOperationResult}
                            columns={generateBulkApproverPreviewColumns(executeCompleted)}
                            entityState={previewEntityState}
                            selectionMode={executeCompleted ? SelectionMode.none : SelectionMode.multiple}
                            selection={(items: BulkApproverPreviewRow[]) => !executeCompleted && setSelectedPreviewRows(items)}
                        />
                    ) : (
                        <Stack styles={compactInfoBoxStyles}>
                            <Text>No eligible packages were found for this operation.</Text>
                        </Stack>
                    )}

                    {!executeCompleted && displayedSkippedCount > 0 && (
                        <Stack tokens={{ childrenGap: 6 }}>
                            <Text style={{ color: "#605e5c" }}>
                                These packages cannot be updated because the selected user is the only approver in a required stage.
                            </Text>
                            <StandardList
                                title="Cannot Remove Approver"
                                entities={skippedRows}
                                columns={generateBulkApproverSkippedColumns()}
                                entityState="LOADED_ALL"
                                selectionMode={SelectionMode.none}
                                selection={() => {}}
                            />
                        </Stack>
                    )}

                </Stack>
            )}

            </Stack>
        </Stack>
    );
}

export default BulkApproverUpdate;
